import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { useApp } from "@/context/AppContext";
import { fmtDate } from "@/lib/time";
import { Btn, SalesHeader } from "@/components/sales/ui";

const Row = ({ label, value, testid }) => <div className="flex justify-between border-b py-2 text-sm last:border-0"><span className="text-muted-foreground">{label}</span><span data-testid={testid}>{value ?? "–"}</span></div>;

const Services = () => {
  const { run } = useApp();
  const d = useFetch("/sales/settings/services");
  const [t, setT] = useState("");
  useEffect(() => { if (d) setT(d.items.join("\n")); }, [d]);
  return (
    <section className="rounded-2xl border bg-card/70 p-5">
      <h2 className="font-display text-lg font-semibold">Atom IP services</h2>
      <p className="mb-2 text-xs text-muted-foreground">One per line. The AI only suggests services from this list.</p>
      <Textarea rows={5} value={t} onChange={(e) => setT(e.target.value)} data-testid="services-input" />
      <Btn className="mt-2" onClick={() => run(() => api.put("/sales/settings/services", { items: t.split("\n") }), "Services saved")} data-testid="services-save-btn">Save</Btn>
    </section>
  );
};

export default function SalesSettings() {
  const { refresh } = useApp();
  const st = useFetch("/sales/settings/insight");
  const usage = useFetch("/sales/settings/ai-usage");
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState("");
  const go = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "test") setTest((await api.post("/sales/settings/insight/test")).data);
      else { await api.post("/sales/settings/insight/sync"); toast.success("Sync started. This can take a minute."); setTimeout(refresh, 8000); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
    refresh();
  };
  const last = test || st?.last_test;
  return (
    <div data-testid="sales-settings-page">
      <SalesHeader title="Settings" subtitle="Integrations → Atom Insight, AI costs and the services we sell." />
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-2xl border bg-card/70 p-5" data-testid="insight-settings">
          <div className="mb-3 flex items-center justify-between"><h2 className="font-display text-lg font-semibold">Atom Insight</h2>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${last?.ok ? "bg-emerald-500/15 text-emerald-300" : last ? "bg-red-500/15 text-red-300" : "bg-white/5 text-muted-foreground"}`} data-testid="insight-status-badge">{last?.ok ? "Connected" : last ? "Problem" : "Not tested"}</span></div>
          {!st ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>
            <Row label="Connected account" value={st.account} />
            <Row label="Last successful connection" value={st.last_ok_at && fmtDate(st.last_ok_at)} />
            <Row label="Last successful sync" value={st.last_sync_at ? fmtDate(st.last_sync_at) : st.sync_running ? "Running…" : "Never"} testid="insight-last-sync" />
            <Row label="Calls imported" value={st.calls_imported} testid="insight-calls-imported" />
            <Row label="Companies matched" value={st.companies_matched} />
            <Row label="Unmatched calls" value={st.calls_unmatched} />
            {st.calls_imported > 0 && st.companies_matched === 0 && <p className="mt-2 text-xs text-muted-foreground" data-testid="insight-match-hint">Calls link to companies automatically once a company or contact has the same phone number. Import your leads or add numbers to start matching.</p>}</>)}
          <div className="mt-4 flex gap-2">
            <Btn variant="cyan" onClick={() => go("test")} disabled={!!busy} data-testid="insight-test-btn">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Test Connection</Btn>
            <Btn onClick={() => go("sync")} disabled={!!busy || st?.sync_running} data-testid="insight-sync-btn"><RefreshCw className="h-4 w-4" /> Sync now</Btn>
          </div>
          {last?.steps && <ul className="mt-4 space-y-1.5 text-sm" data-testid="insight-test-steps">{last.steps.map((s) => (
            <li key={s.step} className="flex gap-2">{s.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />}<span><b>{s.step}</b> <span className="text-muted-foreground">— {s.detail}</span></span></li>))}</ul>}
          {st?.errors?.length > 0 && <div className="mt-4"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recent errors</p>{st.errors.slice(0, 5).map((e, i) => <p key={i} className="text-xs text-red-300">{fmtDate(e.at)} · {e.message}</p>)}</div>}
        </section>
        <div className="space-y-6">
          <section className="rounded-2xl border bg-card/70 p-5" data-testid="ai-usage">
            <h2 className="font-display text-lg font-semibold">AI usage</h2>
            <p className="mb-2 text-xs text-muted-foreground">Model {usage?.model}. AI only runs when someone clicks; results are reused until the underlying data changes. Costs are estimates.</p>
            <Row label="Total AI requests" value={usage?.calls} />
            <Row label="Estimated cost" value={usage ? `$${usage.total_cost_usd.toFixed(4)}` : null} testid="ai-total-cost" />
            {usage && Object.entries(usage.by_kind).map(([k, v]) => <Row key={k} label={k.replace(/_/g, " ")} value={`${v.calls} · ~${v.tokens.toLocaleString()} tokens · $${v.cost_usd.toFixed(4)}`} />)}
          </section>
          <Services />
        </div>
      </div>
    </div>
  );
}
